/**
 * What's Coming Online Panel
 * 
 * A read-only informational panel showing platform module release status.
 * Admin users can edit this panel to update status globally.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Rocket, Settings, Plus, Trash2, GripVertical, Loader2, CheckCircle, Clock, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status icon mapping
const STATUS_ICONS = {
  'Internal Testing': <Wrench className="h-3.5 w-3.5 text-amber-500" />,
  'In Progress': <Clock className="h-3.5 w-3.5 text-blue-500" />,
  'Planned': <Rocket className="h-3.5 w-3.5 text-slate-400" />,
  'Beta': <CheckCircle className="h-3.5 w-3.5 text-purple-500" />,
  'Live': <CheckCircle className="h-3.5 w-3.5 text-green-500" />,
};

// Status color mapping
const STATUS_COLORS = {
  'Internal Testing': 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
  'In Progress': 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  'Planned': 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20',
  'Beta': 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
  'Live': 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
};

export default function WhatsComingOnlinePanel({ embedded = false }) {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({ items: [], enabled: true, title: '' });
  const [saving, setSaving] = useState(false);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  // Fetch config on mount
  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/platform/dashboard-status`);
      setConfig(response.data);
      setEditForm({
        items: response.data.items || [],
        enabled: response.data.enabled !== false,
        title: response.data.title || "What's Coming Online"
      });
    } catch (err) {
      console.error('Failed to load dashboard status:', err);
      // Use defaults
      setConfig({
        enabled: true,
        title: "What's Coming Online",
        items: [
          { label: 'Sales Hub', status: 'Internal Testing' },
          { label: 'Ops Hub', status: 'In Progress' },
          { label: 'Finance Hub', status: 'Planned' }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/platform/dashboard-status`, editForm);
      toast.success('Dashboard status updated');
      setConfig({ ...config, ...editForm });
      setEditDialogOpen(false);
    } catch (err) {
      toast.error('Failed to update dashboard status');
    } finally {
      setSaving(false);
    }
  };

  const addItem = () => {
    setEditForm({
      ...editForm,
      items: [...editForm.items, { label: '', status: 'Planned' }]
    });
  };

  const removeItem = (index) => {
    setEditForm({
      ...editForm,
      items: editForm.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...editForm.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setEditForm({ ...editForm, items: newItems });
  };

  // Don't render if disabled or loading
  if (loading) {
    if (embedded) {
      return <div className="h-full flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
    }
    return (
      <Card className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 border-slate-200 dark:border-slate-700">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!config?.enabled) {
    if (embedded) {
      return <div className="h-full flex items-center justify-center text-muted-foreground text-sm">Panel disabled</div>;
    }
    return null;
  }

  // Embedded mode for dashboard builder - no Card wrapper, compact view
  if (embedded) {
    return (
      <div className="h-full flex flex-col p-3">
        <div className="space-y-2 overflow-auto">
          {config.items?.map((item, index) => (
            <div 
              key={index}
              className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/50 dark:bg-slate-800/50 border border-slate-200/50 dark:border-slate-700/50"
            >
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {item.label}
              </span>
              <Badge 
                variant="outline" 
                className={`text-xs font-medium ${STATUS_COLORS[item.status] || STATUS_COLORS['Planned']}`}
              >
                <span className="mr-1">{STATUS_ICONS[item.status] || STATUS_ICONS['Planned']}</span>
                {item.status}
              </Badge>
            </div>
          ))}
          {(!config.items || config.items.length === 0) && (
            <div className="text-center py-4 text-muted-foreground text-sm">
              No items configured
            </div>
          )}
        </div>
        {config.updated_at && (
          <p className="text-[10px] text-muted-foreground mt-auto pt-2">
            Last updated: {new Date(config.updated_at).toLocaleDateString()}
          </p>
        )}
      </div>
    );
  }

  return (
    <>
      <Card 
        className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 border-slate-200 dark:border-slate-700"
        data-testid="whats-coming-online-panel"
      >
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Rocket className="h-5 w-5 text-primary" />
              <CardTitle className="text-base">{config.title || "What's Coming Online"}</CardTitle>
            </div>
            {isAdmin && (
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={() => setEditDialogOpen(true)}
                title="Edit panel"
              >
                <Settings className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-2">
            {config.items?.map((item, index) => (
              <div 
                key={index}
                className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/50 dark:bg-slate-800/50 border border-slate-200/50 dark:border-slate-700/50"
              >
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {item.label}
                </span>
                <Badge 
                  variant="outline" 
                  className={`text-xs ${STATUS_COLORS[item.status] || STATUS_COLORS['Planned']}`}
                >
                  <span className="mr-1">{STATUS_ICONS[item.status] || STATUS_ICONS['Planned']}</span>
                  {item.status}
                </Badge>
              </div>
            ))}
          </div>
          {config.updated_at && (
            <p className="text-[10px] text-muted-foreground mt-3 text-right">
              Last updated: {new Date(config.updated_at).toLocaleDateString()}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Admin Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Edit "What's Coming Online"
            </DialogTitle>
            <DialogDescription>
              Changes will be visible to all users immediately.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Show panel to users</span>
              <Button
                variant={editForm.enabled ? "default" : "outline"}
                size="sm"
                onClick={() => setEditForm({ ...editForm, enabled: !editForm.enabled })}
              >
                {editForm.enabled ? 'Enabled' : 'Disabled'}
              </Button>
            </div>

            {/* Panel Title */}
            <div>
              <label className="text-sm font-medium">Panel Title</label>
              <Input
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                placeholder="What's Coming Online"
                className="mt-1"
              />
            </div>

            {/* Items */}
            <div>
              <label className="text-sm font-medium">Status Items</label>
              <div className="space-y-2 mt-2">
                {editForm.items.map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <GripVertical className="h-4 w-4 text-muted-foreground cursor-move" />
                    <Input
                      value={item.label}
                      onChange={(e) => updateItem(index, 'label', e.target.value)}
                      placeholder="Module name"
                      className="flex-1"
                    />
                    <select
                      value={item.status}
                      onChange={(e) => updateItem(index, 'status', e.target.value)}
                      className="h-9 px-2 rounded-md border border-input bg-background text-sm"
                    >
                      <option value="Planned">Planned</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Internal Testing">Internal Testing</option>
                      <option value="Beta">Beta</option>
                      <option value="Live">Live</option>
                    </select>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-500 hover:text-red-600"
                      onClick={() => removeItem(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addItem} className="w-full">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Item
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
